import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { makeStyles, tokens } from '@fluentui/react-components';
import { uploadFilingAttachment, extractFilingDocument, docKindsFor, removeFilingAttachment } from '../../services/complianceApi';
import type { FilingExtractionResult, FilingDocument } from '../../types';
import DocumentDropSlot from '../upload/DocumentDropSlot';
import { routeDrop, type FilingDocumentKind } from '../upload/uploadRules';

type Kind = FilingDocumentKind;

const useStyles = makeStyles({
  root: { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap', alignItems: 'flex-end' },
});

interface Props {
  taskId: string;
  clientId: string;
  obligationId: string;
  obligationType: string;
  /** Documents already attached to this filing — drives the on-file indicator + remove per kind. */
  attachments?: FilingDocument[];
  onExtracted: (r: FilingExtractionResult) => void;
  onUploaded: () => void;
  /** Called after a successful remove so the parent can refetch the task/extracts. */
  onRemoved?: () => void;
  // Test seams.
  _uploadFn?: typeof uploadFilingAttachment;
  _extractFn?: typeof extractFilingDocument;
  _removeFn?: typeof removeFilingAttachment;
}

export default function UploadExtractControl(props: Props) {
  const { taskId, clientId, obligationId, obligationType, attachments, onExtracted, onUploaded, onRemoved } = props;
  const styles = useStyles();
  const { t } = useTranslation();
  const { accounts } = useMsal();
  const account = accounts[0];
  const [busyKind, setBusyKind] = useState<string | null>(null);

  const upload = props._uploadFn ?? uploadFilingAttachment;
  const extract = props._extractFn ?? extractFilingDocument;
  const remove = props._removeFn ?? removeFilingAttachment;
  const kinds = docKindsFor(obligationType);

  /** Newest on-file doc of a given kind (for the indicator + remove target). */
  function docForKind(kind: string): FilingDocument | undefined {
    const of = (attachments ?? []).filter((d) => d.kind === kind);
    return of.length ? of[of.length - 1] : undefined;
  }

  async function onRemove(blobName: string) {
    setBusyKind('__removing__');
    try {
      await remove(taskId, obligationId, clientId, blobName);
      onRemoved?.();
    } catch (err) {
      onExtracted({
        ok: false, taxTypeMatch: false, crossCheckPassed: false, confidence: 0,
        message: err instanceof Error ? err.message : t('uploadExtract.failed'),
      });
    } finally {
      setBusyKind(null);
    }
  }

  async function onPick(kind: Kind, file: File) {
    setBusyKind(kind);
    try {
      await upload(taskId, obligationId, {
        clientId, file, kind, uploadedBy: account?.name ?? account?.username,
      });
      onUploaded();
      const result = await extract(taskId, obligationId, {
        clientId, kind, enteredByName: account?.name, enteredById: account?.localAccountId,
      });
      onExtracted(result);
    } catch (err) {
      onExtracted({
        ok: false, taxTypeMatch: false, crossCheckPassed: false, confidence: 0,
        message: err instanceof Error ? err.message : t('uploadExtract.failed'),
      });
    } finally {
      setBusyKind(null);
    }
  }

  // Dropped or picked files for one slot. Each file is validated up front, and several at once are each
  // typed from their filenames (`routeDrop`). A refused file is named, never sent; a placed one uploads to
  // its own slot, one after another.
  function onFiles(kind: Kind, files: File[]): boolean {
    if (busyKind !== null) return false;
    const { placed, refused } = routeDrop(files, kind, kinds);
    // The refusal is said LAST: the page has one message bar, and a placed file's extraction result would
    // otherwise overwrite it. A placed file's own outcome is its slot's ✓ name.
    void (async () => {
      for (const p of placed) await onPick(p.kind, p.file);
      if (refused.length > 0) {
        onExtracted({
          ok: false, taxTypeMatch: false, crossCheckPassed: false, confidence: 0,
          message: refused.join(' '),
        });
      }
    })();
    return placed.some((p) => p.kind === kind);
  }

  return (
    <div className={styles.root}>
      {kinds.map((kind) => {
        const doc = docForKind(kind);
        const disabled = busyKind !== null && busyKind !== kind;
        return (
          <DocumentDropSlot
            key={kind}
            testId={`reconcile-dropzone-${kind}`}
            label={t(`uploadExtract.kind.${kind}`)}
            busy={busyKind === kind || busyKind === '__removing__'}
            disabled={disabled}
            uploadedName={doc?.fileName}
            onFiles={(files) => onFiles(kind, files)}
            onRemove={doc ? () => { void onRemove(doc.blobName); } : undefined}
            removeLabel={t('common.remove')}
          />
        );
      })}
    </div>
  );
}
